import React, { useState, useEffect } from 'react'
import Popup from '../../components/Popup'
import './SupportPopup.scss'
import * as API from '../../services/apis'

interface SupportConfig {
  title?: string
  primaryColor?: string
  tabs?: {
    bestPractices?: {
      tabLabel?: string
      content?: string[]
      buttonText?: string
      contentTitle?: string
    }
    report?: {
      tabLabel?: string
      content?: string[]
      buttonText?: string
      contentTitle?: string
      placeholder?: string
      successText?: string
      maxLength?: number;
    }
  }
  apiEndpoint?: string
  autoCloseDelay?: number
}

interface SupportPopupProps {
  isVisible: boolean
  onClose: () => void
  onScanAgain?: () => void
  config: SupportConfig
  languageConfig?: any
  sessionId?: string
  productInfo?: any
  posthogEvent?: any
}

const SupportPopup: React.FC<SupportPopupProps> = ({
  isVisible,
  onClose,
  onScanAgain,
  config,
  languageConfig = {},
  sessionId = '',
  productInfo = {},
  posthogEvent,
}) => {
  const [activeTab, setActiveTab] = useState<'bestPractices' | 'report'>('bestPractices')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null)
  const [reportText, setReportText] = useState('')
  const [errorSubmitting, setErrorSubmitting] = useState(false)

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReportText(e.target.value)
  }

  useEffect(() => {
    if (isVisible && posthogEvent) {
      posthogEvent.sendPostHogEvent('support_popup_opened', {
        sessionId,
        timestamp: new Date().toISOString(),
      })
    }
  }, [isVisible, posthogEvent, sessionId])
  
    useEffect(() => {
    if (!isVisible) return

    document.body.style.transition = 'transform 0.2s ease-out'

    const handleViewportChange = () => {
      if (window.visualViewport) {
        const { height, pageTop } = window.visualViewport

        document.body.style.transform = `translateY(${pageTop}px)`
        document.documentElement.style.setProperty('--vh', `${height * 0.01}px`)
      }
    }

    handleViewportChange()

    // if (window.visualViewport) {
    //   window.visualViewport.addEventListener('resize', handleViewportChange)
    // }

    return () => {
      // Reset with transition
      document.body.style.transform = ''

      // Remove transition after a delay
      setTimeout(() => {
        document.body.style.transition = ''
      }, 200)

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange)
      }
    }
  }, [isVisible])
  
  const handleTabSwitch = (tab: 'bestPractices' | 'report') => {
    setActiveTab(tab)
    setIsSubmitted(false)

    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer)
      setAutoCloseTimer(null)
    }

    if (posthogEvent) {
      posthogEvent.sendPostHogEvent('support_popup_tab_switched', {
        sessionId,
        fromTab: activeTab,
        toTab: tab,
        timestamp: new Date().toISOString(),
      })
    }
  }
  const getMaxLength = () => config?.tabs?.report?.maxLength || 1024
  const remainingChars = getMaxLength() - reportText.length

  const handlePrimaryButtonClick = async () => {
    if (activeTab === 'bestPractices') {
      if (posthogEvent) {
        posthogEvent.sendPostHogEvent('support_popup_try_again_clicked', {
          sessionId,
          timestamp: new Date().toISOString(),
        })
      }
      onClose()
      onScanAgain?.()
    } else {
      if (posthogEvent) {
        posthogEvent.sendPostHogEvent('support_popup_report_issue_clicked', {
          sessionId,
          timestamp: new Date().toISOString(),
        })
      }

      setIsSubmitting(true)

      try {
        const response = await API.supportTicket(
          sessionId,
          reportText,
          productInfo.id,
        )

        if (response.success === true) {
          setIsSubmitted(true)

          if (posthogEvent) {
            posthogEvent.sendPostHogEvent('support_popup_report_issue_success', {
              sessionId,
              timestamp: new Date().toISOString(),
            })
          }

          const delay = (config?.autoCloseDelay || 5) * 1000
          const timer = setTimeout(() => {
            onClose()
          }, delay)
          setAutoCloseTimer(timer)
        } else {
          setErrorSubmitting(true)
          if (posthogEvent) {
            posthogEvent.sendPostHogEvent('support_popup_report_issue_failed', {
              sessionId,
              error: 'No success response',
              timestamp: new Date().toISOString(),
            })
          }
          throw new Error('Failed to submit report')
        }
      } catch (error) {
        console.error('Error submitting support report:', error)
        setErrorSubmitting(true)
        if (posthogEvent) {
          posthogEvent.sendPostHogEvent('support_popup_report_issue_failed', {
            sessionId,
            error: error.message,
            timestamp: new Date().toISOString(),
          })
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleClose = () => {
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer)
      setAutoCloseTimer(null)
    }

    if (posthogEvent) {
      posthogEvent.sendPostHogEvent('support_popup_closed', {
        sessionId,
        activeTab,
        wasSubmitted: isSubmitted,
        timestamp: new Date().toISOString(),
      })
    }

    onClose()
  }

  useEffect(() => {
    return () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer)
      }
    }
  }, [autoCloseTimer])

  const getPrimaryColor = () => config?.primaryColor || '#6366f1'

  const parseTextWithBold = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g)

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2)
        return (
          <strong key={index} className='support-popup__bold-text'>
            {boldText}
          </strong>
        )
      }
      return part
    })
  }

  const getBestPracticesContent = () => {
    return (
      config?.tabs?.bestPractices?.content || [
        'Check internet **speed**: **500 kbps** required',
        'Check **lighting**: **Better lighting** = quicker results',
        'Hold phone **still**: **Stable hands** = quicker results',
      ]
    )
  }

  const getReportContent = () => {
    return (
      config?.tabs?.report?.content || [
        'Describe your issues - add details like **lighting conditions** & **internet speed**',
      ]
    )
  }

  const getCurrentButtonText = () => {
    if (activeTab === 'bestPractices') {
      return config?.tabs?.bestPractices?.buttonText || 'Try Again'
    } else {
      if (isSubmitted) {
        return 'Issue Reported ✓'
      }
      return config?.tabs?.report?.buttonText || 'Report Issue'
    }
  }

  const renderTabContent = () => {
    if (activeTab === 'bestPractices') {
      return (
        <div className='support-popup__content'>
          <div className='support-popup__question' style={{ color: getPrimaryColor() }}>
            {config?.tabs?.bestPractices?.contentTitle || 'Are the scans taking too long?'}
          </div>
          <div className='support-popup__tips'>
            {getBestPracticesContent().map((tip, index) => (
              <div key={index} className='support-popup__tip'>
                <span className='support-popup__tip-bullet'>•</span>
                <span className='support-popup__tip-text'>{parseTextWithBold(tip)}</span>
              </div>
            ))}
          </div>
        </div>
      )
    } else {
      return (
        <div className='support-popup__content'>
          <div className='support-popup__question' style={{ color: getPrimaryColor() }}>
            {config?.tabs?.report?.contentTitle || 'Unhappy with the results?'}
          </div>
          <div className='support-popup__report-content'>
            {/* {getReportContent().map((content, index) => (
                <div key={index} className="support-popup__report-text">
                  {parseTextWithBold(content)}
                </div>
              ))} */}
            <textarea
              className='support-popup__textarea'
              placeholder={
                config?.tabs?.report?.placeholder ||
                'Describe your issues - add details like lighting conditions & internet speed'
              }
              value={reportText}
              onChange={handleTextareaChange}
              rows={4}
              maxLength={getMaxLength()}
              onContextMenu={(e) => e.stopPropagation()}
            />
            <div className='support-popup__char-counter'>
              <span className={remainingChars < 0 ? 'support-popup__char-counter--error' : ''}>
                {reportText.length}/{getMaxLength()}
              </span>
            </div>
          </div>
        </div>
      )
      //}
    }
  }

  return (
    <Popup
      isVisible={isVisible}
      onClose={handleClose}
      className='support-popup'
      showCloseButton={true}
      maxWidth='100vw'
      height='500px'
      showFooter={false}
    >
      <div className='support-popup__container'>
        <div className='support-popup__header'>
          <h2 className='support-popup__title'>{config?.title || 'Support'}</h2>
        </div>
        {isSubmitted || errorSubmitting ? (
          <div className='support-popup__content' style={{ height: '100%' }}>
            <div className='support-popup__success'>
              {/* <div className='support-popup__success-icon'>✓</div> */}
              <div
                style={{
                  borderRadius: '8px',
                  backgroundColor: '#F9F9F9',
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div className='support-popup__success-text'>
                  {errorSubmitting
                    ? 'Something went wrong. Please try after sometime'
                    : config?.tabs?.report?.successText ?? 'Issue Reported ✅'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='support-popup__tabs'>
              <button
                className={`support-popup__tab ${
                  activeTab === 'bestPractices' ? 'support-popup__tab--active' : ''
                }`}
                onClick={() => handleTabSwitch('bestPractices')}
                style={
                  activeTab === 'bestPractices'
                    ? {
                        color: getPrimaryColor(),
                        borderBottomColor: getPrimaryColor(),
                      }
                    : {}
                }
              >
                {config?.tabs?.bestPractices?.tabLabel || 'Best Practices'}
              </button>

              <button
                className={`support-popup__tab ${
                  activeTab === 'report' ? 'support-popup__tab--active' : ''
                }`}
                onClick={() => handleTabSwitch('report')}
                style={
                  activeTab === 'report'
                    ? {
                        color: getPrimaryColor(),
                        borderBottomColor: getPrimaryColor(),
                      }
                    : {}
                }
              >
                {config?.tabs?.report?.tabLabel || 'Report'}
              </button>
            </div>
            <div className='support-popup__body'>{renderTabContent()}</div>

            <div className='support-popup__footer'>
              <button
                className='support-popup__primary-button'
                onClick={handlePrimaryButtonClick}
                disabled={
                  isSubmitting ||
                  isSubmitted ||
                  (reportText.trim().length === 0 && activeTab === 'report') ||
                  (reportText.length > getMaxLength() && activeTab === 'report')
                }
                style={{ backgroundColor: getPrimaryColor() }}
              >
                {isSubmitting ? 'Submitting...' : getCurrentButtonText()}
              </button>
            </div>
          </>
        )}
      </div>
    </Popup>
  )
}

export default SupportPopup
